/**
 * Contains helper functions used by page actions in Vuex store.
 *
 * @module
 */

import axios from 'axios';
import { Commit, Dispatch } from 'vuex';
import { router } from '@/main/router/';
import { PageStoreBase } from '@/main/store/@types/page';

import _ from 'lodash';
import { ERROR } from './actionTypes';
import {
	UPDATE_BASIC_EDIT, UPDATE_BASIC_FILTER, UPDATE_BASIC_TABLE_SCHEMA, UPDATE_BASIC_TABLE_DATA,
	ROW_DETAIL_LOADING, ROW_DETAIL, START_LOADING, END_LOADING, UPDATE_EDIT_HASH,
} from './listEditTypes';
import { VUE_APP_TENANT_HOST } from './constants';

/**
 * Sets component's loading state to true, waits for `servicePromise` to fulfill,
 * commits the {@link UPDATE_BASIC_FILTER} mutation with data from the promise
 * and sets loading to false.
 *
 * In case the promise rejects, commits the {@link ERROR} mutation.
 *
 * @param state - Vuex state of a page.
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @returns response from `servicePromise` or error.
 */
export async function updateFilterAction(
	state: PageStoreBase,
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
) {
	try {
		commit(START_LOADING);
		const resp = await servicePromise;
		commit(UPDATE_BASIC_FILTER, resp.data);
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { objectName: state.name, err }, { root: true });
		return err;
	} finally {
		commit(END_LOADING);
	}
}

/**
 * Waits for the `servicePromise` to fulfill and commits the
 * {@link UPDATE_BASIC_TABLE_SCHEMA} mutation with data from the promise.
 *
 * @param state - Vuex state of a page.
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @param tableName - name of the affected table.
 * @returns response from `servicePromise` or error.
 */
export async function updateTableSchemaAction(
	state: PageStoreBase,
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
	tableName: string,
) {
	try {
		const resp = await servicePromise;
		commit(UPDATE_BASIC_TABLE_SCHEMA, { data: resp.data, tableName });
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { objectName: state.name, err }, { root: true });
		return err;
	}
}

/**
 * Waits for the `servicePromise` to fulfill and commits the
 * {@link UPDATE_BASIC_TABLE_DATA} mutation with data from the promise.
 *
 * @param state - Vuex state of a page.
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @param tableName - name of the affected table.
 * @returns response from `servicePromise` or error.
 */
export async function updateTableDataAction(
	state: PageStoreBase,
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
	tableName: string = 'table',
) {
	try {
		commit(START_LOADING, tableName);
		const resp = await servicePromise;
		commit(UPDATE_BASIC_TABLE_DATA, { tableName, data: resp.data });
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { objectName: state.name, err }, { root: true });
		return err;
	} finally {
		commit(END_LOADING, tableName);
	}
}

/**
 * Sets component's loading state to true, waits for `servicePromise` to fulfill,
 * commits the {@link UPDATE_BASIC_EDIT} mutation with data from the promise and
 * sets loading to false.
 *
 * In case `servicePromise` rejects, commits the {@link ERROR} mutation.
 *
 * @param state - Vuex state of a page.
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @returns response from `servicePromise` or error.
 */
export async function updateEditAction(
	state: PageStoreBase,
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
) {
	try {
		commit(START_LOADING);
		const resp = await servicePromise;
		commit(UPDATE_BASIC_EDIT, { data: resp.data });
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { objectName: state.name, err }, { root: true });
		return err;
	} finally {
		commit(END_LOADING);
	}
}

/**
 * Same as {@link updateEditAction} but does not set the the component's loading property.
 *
 * @param state - Vuex state of a page.
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @returns response from `servicePromise` or error.
 */
export async function updateEditActionNoLoading(
	state: PageStoreBase,
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
) {
	try {
		const resp = await servicePromise;
		commit(UPDATE_BASIC_EDIT, { data: resp.data });
		commit(UPDATE_EDIT_HASH, { data: resp.data });
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { objectName: state.name, err }, { root: true });
		return err;
	}
}

/**
 * Sets component's loading state to true, waits for `servicePromise` to fulfill,
 * calls `router.back()` and sets loading to false.
 *
 * In case `servicePromise` rejects, commits the {@link ERROR} mutation.
 *
 * @param state - Vuex state of a page.
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @param goBack=true - go back to previous page.
 * @returns response from `servicePromise` or error.
 */
export async function writeAction(
	state: PageStoreBase,
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
	goBack = true,
) {
	try {
		commit(START_LOADING);
		const resp = await servicePromise;
		commit(UPDATE_BASIC_EDIT, { data: resp.data });
		commit(UPDATE_EDIT_HASH, { data: resp.data });

		// external modules
		const targetOrigin = !_.isUndefined(VUE_APP_TENANT_HOST[0]?.toString()) && VUE_APP_TENANT_HOST[0]?.toString().length > 0
			? VUE_APP_TENANT_HOST[0]?.toString()
			: `${window.location.protocol}//${window.location.host}`;

		// external modules
		const targetWindow = window.parent;
		targetWindow.postMessage('saved', targetOrigin);
		if (goBack) {
			router.back();
		}
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { objectName: state.name, err }, { root: true });
		return err;
	} finally {
		commit(END_LOADING);
	}
}

/**
 * Sets component's loading state to true, waits for `servicePromise` to fulfill,
 * commits the `commitKey` mutation with `{ data: servicePromiseResp.data, ...additionalData }`
 * and sets loading to false.
 *
 * In case `servicePromise` rejects, commits the {@link ERROR} mutation.
 *
 * @param commit - Vuex commit function.
 * @param dispatch - Vuex dispatch function.
 * @param servicePromise - resolves when requested data arrive.
 * @param tableName - name of the table whose loading state will be affected.
 * @param commitKey - name of the mutation that will be committed.
 * @param additionalData - data that will be added to the committed mutation.
 * @returns response from `servicePromise` or error.
 */
export async function commitAction(
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
	tableName: string,
	commitKey: string,
	additionalData = {},
) {
	try {
		commit(START_LOADING, tableName);
		const resp = await servicePromise;
		commit(commitKey, { data: resp.data, ...additionalData });
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { err }, { root: true });
		return err;
	} finally {
		commit(END_LOADING, tableName);
	}
}

export async function commitRowDetail(
	commit: Commit,
	dispatch: Dispatch,
	servicePromise: Promise<{ data: object }>,
) {
	try {
		commit(ROW_DETAIL_LOADING, true);
		const resp = await servicePromise;
		commit(ROW_DETAIL, { data: resp.data });
		return resp;
	} catch (err) {
		if (axios.isCancel(err)) return null;

		dispatch(ERROR, { err }, { root: true });
		return err;
	} finally {
		commit(ROW_DETAIL_LOADING, false);
	}
}
